import React, { useState } from 'react';
import Hero from '../components/Hero';
import { useTranslation } from 'react-i18next';
import SponsorsSection from '../components/SponsorsSection';

type VisitorType = 'spectator' | 'athlete' | 'volunteer' | null;

const BK2026Page: React.FC = () => {
  const { t } = useTranslation();
  const [visitorType, setVisitorType] = useState<VisitorType>(null);

  const travelSection = (bgClass: string) => (
    <section className={`section ${bgClass}`}>
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-dark mb-8">{t('bk2026.travel.title')}</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-lg font-bold text-dark mb-3">{t('bk2026.travel.planeTitle')}</h3>
              <p className="text-gray-700 text-sm mb-2">
                <strong>Brussels Airport (BRU):</strong> {t('bk2026.travel.bruDist')}<br />
                <a href="https://www.brusselsairport.be/en" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">brusselsairport.be</a>
              </p>
              <p className="text-gray-700 text-sm">
                <strong>Antwerp Airport (ANR):</strong> {t('bk2026.travel.anrDist')}<br />
                <a href="https://www.antwerp-airport.be" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">antwerp-airport.be</a>
              </p>
            </div>
            <div>
              <h3 className="text-lg font-bold text-dark mb-3">{t('bk2026.travel.trainTitle')}</h3>
              <p className="text-gray-700 text-sm">
                {t('bk2026.travel.trainText')}<br />
                <a href="https://www.belgiantrain.be/en" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">belgiantrain.be</a>
              </p>
            </div>
            <div>
              <h3 className="text-lg font-bold text-dark mb-3">{t('bk2026.travel.carTitle')}</h3>
              <p className="text-gray-700 text-sm mb-2">{t('bk2026.travel.carText')}</p>
              <a href="https://www.google.com/maps/search/Klimax+Puurs+Belgium" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline text-sm">
                {t('bk2026.travel.googleMaps')}
              </a>
            </div>
            <div>
              <h3 className="text-lg font-bold text-dark mb-3">{t('bk2026.travel.busTitle')}</h3>
              <p className="text-gray-700 text-sm mb-2">{t('bk2026.travel.busStop')}</p>
              <a href="https://www.delijn.be/nl/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline text-sm">
                {t('bk2026.travel.busInfo')}
              </a>
            </div>
          </div>
          <div className="mt-8 bg-blue-50 border-l-4 border-blue-400 p-4 rounded-r-lg text-sm text-gray-700">
            {t('bk2026.travel.accommodationText')}
          </div>
        </div>
      </div>
    </section>
  );

  const programmeTable = (bgClass: string, highlightForSpectators = false) => (
    <section className={`section ${bgClass}`}>
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-dark mb-4">
            {t('bk2026.programme.title')}{' '}
            <span className="text-lg font-normal text-gray-500">{t('bk2026.programme.timeSuffix')}</span>
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <tbody>
                {!highlightForSpectators && (
                  <>
                    <tr className="bg-gray-100">
                      <td colSpan={3} className="px-4 py-3 font-bold text-dark uppercase tracking-wide text-base border-b-2 border-gray-300">
                        {t('bk2026.programme.friday')}
                      </td>
                    </tr>
                    <tr className="border-b border-gray-100">
                      <td className="px-4 py-2 w-20 font-semibold text-gray-500 whitespace-nowrap">21:00</td>
                      <td className="px-2 py-2 w-20 text-gray-400 whitespace-nowrap">22:00</td>
                      <td className="px-4 py-2">{t('bk2026.programme.confirmAttendance')}</td>
                    </tr>
                  </>
                )}
                <tr className="bg-gray-100">
                  <td colSpan={3} className="px-4 py-3 font-bold text-dark uppercase tracking-wide text-base border-b-2 border-gray-300">
                    {t('bk2026.programme.saturday')}
                  </td>
                </tr>
                {([
                  ['9:00','10:00', t('bk2026.programme.registration'), false],
                  ['10:00','12:00', t('bk2026.programme.beginnerLesson'), false],
                  ['12:00','12:30', t('bk2026.programme.briefingLeadSpeed'), false],
                  ['12:30','16:00', t('bk2026.programme.leadQual'), false],
                  ['16:00','17:15', t('bk2026.programme.speedQual'), false],
                  ['17:15','18:00', t('bk2026.programme.speedFinals'), true],
                  ['18:00','19:00', t('bk2026.programme.diner'), true],
                  ['19:00','20:30', t('bk2026.programme.leadFinals'), true],
                  ['20:30','21:00', t('bk2026.programme.podiumLeadSpeed'), true],
                  ['21:00','—', t('bk2026.programme.party'), true],
                ] as [string,string,string,boolean][]).map(([start, end, label, highlight], i) => (
                  <tr key={i} className={`border-b border-gray-100 ${highlightForSpectators && highlight ? 'bg-yellow-100' : ''}`}>
                    <td className="px-4 py-2 font-semibold text-gray-500 whitespace-nowrap">{start}</td>
                    <td className="px-2 py-2 text-gray-400 whitespace-nowrap">{end}</td>
                    <td className="px-4 py-2">{highlightForSpectators && highlight ? <strong>{label}</strong> : label}</td>
                  </tr>
                ))}
                <tr className="bg-gray-100">
                  <td colSpan={3} className="px-4 py-3 font-bold text-dark uppercase tracking-wide text-base border-b-2 border-gray-300">
                    {t('bk2026.programme.sunday')}
                  </td>
                </tr>
                {([
                  ...(!highlightForSpectators ? [['09:00','11:00', t('bk2026.programme.warmUp'), false] as [string,string,string,boolean]] : []),
                  ...(!highlightForSpectators ? [['10:30','11:00', t('bk2026.programme.briefingBoulder'), false] as [string,string,string,boolean]] : []),
                  ['11:00','14:00', t('bk2026.programme.boulderComp'), true] as [string,string,string,boolean],
                  ['14:00','15:00', t('bk2026.programme.podiumBoulder'), true] as [string,string,string,boolean],
                ]).map(([start, end, label, highlight], i) => (
                  <tr key={i} className={`border-b border-gray-100 ${highlightForSpectators && highlight ? 'bg-yellow-100' : ''}`}>
                    <td className="px-4 py-2 font-semibold text-gray-500 whitespace-nowrap">{start}</td>
                    <td className="px-2 py-2 text-gray-400 whitespace-nowrap">{end}</td>
                    <td className="px-4 py-2">{highlightForSpectators && highlight ? <strong>{label}</strong> : label}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {highlightForSpectators && (
            <p className="text-sm text-gray-500 mt-3 italic">{t('bk2026.spectator.programmeLegend')}</p>
          )}
        </div>
      </div>
    </section>
  );

  return (
    <div>
      <Hero
        title={t('bk2026.hero.title')}
        subtitle={t('bk2026.hero.subtitle')}
        backgroundImage="/assets/what-is-paraclimbing-hero.jpg"
      />

      {/* Visitor type banner (shown after selection) */}
      {visitorType !== null && (
        <div className="bg-gray-100 border-b border-gray-200 py-3">
          <div className="container mx-auto px-4 flex items-center gap-2 text-sm">
            <span className="text-gray-500">{t('bk2026.selection.viewingAs')}</span>
            <span className="font-semibold text-dark">{t(`bk2026.selection.${visitorType}`)}</span>
            <span className="mx-2 text-gray-300">|</span>
            <button onClick={() => setVisitorType(null)} className="text-red-600 hover:underline">
              {t('bk2026.selection.changeType')}
            </button>
          </div>
        </div>
      )}

      {/* ── Visitor selection screen ── */}
      {visitorType === null && (
        <section className="section bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl font-bold text-dark mb-3">{t('bk2026.selection.title')}</h2>
              <p className="text-gray-600 mb-10">{t('bk2026.selection.subtitle')}</p>
              <div className="grid sm:grid-cols-3 gap-6">
                {([
                  { key: 'spectator' as VisitorType, icon: '👁️' },
                  { key: 'athlete' as VisitorType, icon: '🧗' },
                  { key: 'volunteer' as VisitorType, icon: '🤝' },
                ]).map(({ key, icon }) => (
                  <button
                    key={key!}
                    onClick={() => setVisitorType(key)}
                    className={`rounded-xl shadow-md p-8 text-center hover:shadow-xl transition-all duration-200 border-2 cursor-pointer w-full ${key === 'spectator' ? 'bg-red-600 border-red-600 text-white hover:bg-red-700 hover:border-red-700' : 'bg-white border-transparent hover:border-red-500'}`}
                  >
                    <div className="text-5xl mb-4">{icon}</div>
                    <h3 className={`text-xl font-bold mb-2 ${key === 'spectator' ? 'text-white' : 'text-dark'}`}>{t(`bk2026.selection.${key}`)}</h3>
                    <p className={`text-sm ${key === 'spectator' ? 'text-red-100' : 'text-gray-500'}`}>{t(`bk2026.selection.${key}Desc`)}</p>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}


      {/* ══ SPECTATOR CONTENT ══ */}
      {visitorType === 'spectator' && (
        <>
          {/* Hero intro */}
          <section className="section bg-white">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto">
                <div className="flex flex-wrap justify-between items-center gap-4 mb-4">
                  <h2 className="text-3xl font-bold text-dark">{t('bk2026.spectator.welcomeTitle')}</h2>
                  <a
                    href="https://paraclimbing-be.weticket.io/open-belgian-championship-paraclimbing"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block bg-red-600 text-white font-bold px-6 py-3 rounded-xl hover:bg-red-700 transition-colors whitespace-nowrap"
                  >
                    {t('bk2026.spectator.ticketLinkBtn')}
                  </a>
                </div>
                <p className="text-gray-700 text-lg leading-relaxed mb-10">{t('bk2026.spectator.welcomeText')}</p>

                <div className="grid md:grid-cols-2 gap-6">
                  {/* Saturday */}
                  <div className="bg-red-50 border-l-4 border-red-500 rounded-r-xl p-6">
                    <h3 className="text-lg font-bold text-dark mb-2">🧗 {t('bk2026.spectator.saturdayTitle')}</h3>
                    <p className="text-gray-700 text-sm leading-relaxed">{t('bk2026.spectator.saturdayText')}</p>
                  </div>
                  {/* Sunday */}
                  <div className="bg-orange-50 border-l-4 border-orange-400 rounded-r-xl p-6">
                    <h3 className="text-lg font-bold text-dark mb-2">🪨 {t('bk2026.spectator.sundayTitle')}</h3>
                    <p className="text-gray-700 text-sm leading-relaxed">{t('bk2026.spectator.sundayText')}</p>
                  </div>
                  {/* International athletes */}
                  <div className="bg-blue-50 border-l-4 border-blue-400 rounded-r-xl p-6">
                    <h3 className="text-lg font-bold text-dark mb-2">🌍 {t('bk2026.spectator.internationalTitle')}</h3>
                    <p className="text-gray-700 text-sm leading-relaxed">{t('bk2026.spectator.internationalText')}</p>
                  </div>
                  {/* Initiation */}
                  <div className="bg-green-50 border-l-4 border-green-500 rounded-r-xl p-6">
                    <h3 className="text-lg font-bold text-dark mb-2">✋ {t('bk2026.spectator.initiationTitle')}</h3>
                    <p className="text-gray-700 text-sm leading-relaxed">{t('bk2026.spectator.initiationText')}</p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {programmeTable('bg-gray-50', true)}

          {/* Tickets CTA */}
          <section className="section bg-white">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto">
                <div className="bg-red-600 rounded-2xl p-8 text-center text-white">
                  <p className="text-3xl font-bold mb-6">{t('bk2026.spectator.ticketLinkLabel')}</p>
                  <a
                    href="https://paraclimbing-be.weticket.io/open-belgian-championship-paraclimbing"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block bg-white text-red-600 font-bold text-lg px-10 py-4 rounded-xl hover:bg-gray-100 transition-colors"
                  >
                    {t('bk2026.spectator.ticketLinkBtn')}
                  </a>
                </div>
              </div>
            </div>
          </section>

          {travelSection('bg-gray-50')}

          {/* Healthcare */}
          <section className="section bg-white">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto">
                <h2 className="text-3xl font-bold text-dark mb-6">{t('bk2026.health.title')}</h2>
                <p className="text-gray-700 mb-4">{t('bk2026.health.description')}</p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
                  {([
                    { labelKey: 'bk2026.health.ambulance', number: '100' },
                    { labelKey: 'bk2026.health.fire', number: '100' },
                    { labelKey: 'bk2026.health.police', number: '101' },
                    { labelKey: 'bk2026.health.euro', number: '112' },
                  ] as {labelKey:string;number:string}[]).map(({ labelKey, number }) => (
                    <div key={labelKey} className="bg-gray-50 rounded-lg shadow-sm p-4 text-center">
                      <p className="text-gray-500 text-xs mb-1">{t(labelKey)}</p>
                      <p className="text-2xl font-bold text-dark">{number}</p>
                    </div>
                  ))}
                </div>
                <p className="text-gray-700 text-sm">
                  <strong>{t('bk2026.health.hospitalLabel')}</strong> AZ Rivierenland - campus Bornem, Kasteelstraat 23, 2880 Bornem, Belgium
                </p>
              </div>
            </div>
          </section>
        </>
      )}

      {/* ══ VOLUNTEER CONTENT ══ */}
      {visitorType === 'volunteer' && (
        <>
          <section className="section bg-white">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto">
                <h2 className="text-3xl font-bold text-dark mb-6">{t('bk2026.volunteer.title')}</h2>
                <p className="text-gray-700 text-lg leading-relaxed mb-8">{t('bk2026.volunteer.intro')}</p>

                <h3 className="text-xl font-bold text-dark mb-4">{t('bk2026.volunteer.perksTitle')}</h3>
                <ul className="space-y-3 mb-10">
                  {(['perk1','perk2','perk3','perk4'] as const).map(perk => (
                    <li key={perk} className="flex items-start gap-3">
                      <span className="text-green-600 font-bold text-lg mt-0.5">✓</span>
                      <span className="text-gray-700">{t(`bk2026.volunteer.${perk}`)}</span>
                    </li>
                  ))}
                </ul>

                <div className="bg-red-50 border-l-4 border-red-600 p-6 rounded-r-lg">
                  <h3 className="text-xl font-bold text-dark mb-3">{t('bk2026.volunteer.interestTitle')}</h3>
                  <p className="text-gray-700 mb-4">
                    {t('bk2026.volunteer.contactText')}{' '}
                    <a href="mailto:info@paraclimbing.be?subject=Volunteer%20BK%202026" className="text-blue-600 hover:underline font-semibold">
                      info@paraclimbing.be
                    </a>{' '}
                    {t('bk2026.volunteer.contactSubject')}.
                  </p>
                  <a
                    href="https://static.twizzit.com/v2/public/form/317e2e5a35ee638c6b1eb87c91e2e87a"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block bg-red-600 text-white font-bold py-3 px-8 rounded-lg hover:bg-red-700 transition-colors"
                  >
                    {t('bk2026.volunteer.signupButton')}
                  </a>
                </div>
              </div>
            </div>
          </section>

          {/* Organisation info so volunteers know who to contact */}
          <section className="section bg-gray-50">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto">
                <h2 className="text-3xl font-bold text-dark mb-8">{t('bk2026.org.title')}</h2>
                <div className="grid md:grid-cols-2 gap-8">
                  <div>
                    <h3 className="text-lg font-bold text-dark mb-2">{t('bk2026.org.localAuthority')}</h3>
                    <p className="text-gray-700 mb-4">BVKB &amp; Paraclimbing.be</p>
                    <p className="text-gray-700 mb-1">
                      <strong>Event Director BVKB:</strong> Lise Van Essche<br />
                      <a href="mailto:lise.vanessche@bvkb.be" className="text-blue-600 hover:underline">lise.vanessche@bvkb.be</a><br />
                      +32 472 831 653
                    </p>
                    <p className="text-gray-700 mt-3">
                      <strong>Event Director Paraclimbing.be:</strong> Frederik Leys<br />
                      <a href="mailto:info@paraclimbing.be" className="text-blue-600 hover:underline">info@paraclimbing.be</a><br />
                      +32 489 410 092 (WhatsApp only)
                    </p>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-dark mb-2">{t('bk2026.org.venueTitle')}</h3>
                    <p className="text-gray-700">
                      <strong>Klimzaal Klimax</strong><br />
                      Vrijhalsweg 17<br />
                      2870 Breendonk, Belgium<br />
                      <a href="https://www.bvkb.be/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">www.bvkb.be</a>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {programmeTable('bg-white')}
          {travelSection('bg-gray-50')}
        </>
      )}

      {/* ══ ATHLETE / TEAM STAFF CONTENT ══ */}
      {visitorType === 'athlete' && (
        <>
          {/* Download Info Sheet */}
          <section className="bg-red-600 py-6">
        <div className="container mx-auto px-4 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-white font-semibold text-lg">
            {t('bk2026.download.text')}
          </p>
          <a
            href="/assets/infosheet_puurs_full.pdf"
            download
            className="bg-white text-red-600 font-bold py-2 px-6 rounded-lg hover:bg-gray-100 transition-colors whitespace-nowrap"
          >
            {t('bk2026.download.button')}
          </a>
        </div>
      </section>

      {/* Organisation */}
      <section className="section bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-dark mb-8">{t('bk2026.org.title')}</h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-lg font-bold text-dark mb-2">{t('bk2026.org.localAuthority')}</h3>
                <p className="text-gray-700 mb-4">BVKB &amp; Paraclimbing.be</p>
                <p className="text-gray-700 mb-1">
                  <strong>Event Director BVKB:</strong> Lise Van Essche<br />
                  <a href="mailto:lise.vanessche@bvkb.be" className="text-blue-600 hover:underline">lise.vanessche@bvkb.be</a><br />
                  +32 472 831 653
                </p>
                <p className="text-gray-700 mt-3">
                  <strong>Event Director Paraclimbing.be:</strong> Frederik Leys<br />
                  <a href="mailto:info@paraclimbing.be" className="text-blue-600 hover:underline">info@paraclimbing.be</a><br />
                  +32 489 410 092 (WhatsApp only)
                </p>
              </div>
              <div>
                <h3 className="text-lg font-bold text-dark mb-2">{t('bk2026.org.venueTitle')}</h3>
                <p className="text-gray-700">
                  <strong>Klimzaal Klimax</strong><br />
                  Vrijhalsweg 17<br />
                  2870 Breendonk, Belgium<br />
                  <a href="https://www.bvkb.be/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">www.bvkb.be</a>
                </p>
                <p className="text-gray-700 mt-4">
                  <strong>{t('bk2026.org.disciplines')}</strong> Lead / Top Rope, Speed &amp; Boulder
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Registration */}
      <section className="section bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-dark mb-8">{t('bk2026.reg.title')}</h2>
            <div className="bg-red-50 border-l-4 border-red-600 p-5 rounded-r-lg mb-6">
              <p className="text-gray-800 font-semibold">{t('bk2026.reg.deadlineLabel')} <span className="text-red-600">{t('bk2026.reg.deadlineValue')}</span></p>
            </div>
            <div className="space-y-4 text-gray-700">
              <p>
                <strong>{t('bk2026.reg.paymentLabel')}</strong> {t('bk2026.reg.paymentText')}
              </p>
              <p>
                <strong>{t('bk2026.reg.capLabel')}</strong> {t('bk2026.reg.capText')}
              </p>
              <p>
                <strong>{t('bk2026.reg.formLabel')}</strong>{' '}
                <a
                  href="https://docs.google.com/forms/d/e/1FAIpQLSfNFy7vfKR1P4UWHn_NyNVmwP8k_9KHCgkNe36SzZ9k91ZPWQ/viewform?usp=header"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline font-semibold"
                >
                  {t('bk2026.reg.formLink')}
                </a>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Sport Classes */}
      <section className="section bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-dark mb-6">{t('bk2026.classes.title')}</h2>
            <p className="text-gray-700 mb-6">{t('bk2026.classes.description')}</p>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="border border-gray-300 px-4 py-2 text-left font-bold">{t('bk2026.classes.gender')}</th>
                    {['AL1','AL2','AU2','AU3','RP1','RP2','RP3','B1','B2','B3'].map(c => (
                      <th key={c} className="border border-gray-300 px-3 py-2">{c}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="border border-gray-300 px-4 py-2 font-semibold">{t('bk2026.classes.men')}</td>
                    {[0,1,2,3,4,5,6,7,8,9].map(i => (
                      <td key={i} className="border border-gray-300 px-3 py-2 text-center text-green-700">✓</td>
                    ))}
                  </tr>
                  <tr className="bg-gray-50">
                    <td className="border border-gray-300 px-4 py-2 font-semibold">{t('bk2026.classes.women')}</td>
                    {[0,1,2,3,4,5,6,7,8,9].map(i => (
                      <td key={i} className="border border-gray-300 px-3 py-2 text-center text-green-700">✓</td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-gray-600 text-sm mt-4">
              <strong>{t('bk2026.classes.mergingLabel')}</strong> {t('bk2026.classes.mergingText')}
            </p>
          </div>
        </div>
      </section>

      {/* Competition Format */}
      <section className="section bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-dark mb-8">{t('bk2026.format.title')}</h2>
            <div className="space-y-6">
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h3 className="text-lg font-bold text-dark mb-3">{t('bk2026.format.leadTitle')}</h3>
                <p className="text-gray-700 text-sm leading-relaxed">{t('bk2026.format.leadText')}</p>
              </div>
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h3 className="text-lg font-bold text-dark mb-3">{t('bk2026.format.boulderTitle')}</h3>
                <p className="text-gray-700 text-sm leading-relaxed">{t('bk2026.format.boulderText')}</p>
              </div>
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h3 className="text-lg font-bold text-dark mb-3">{t('bk2026.format.speedTitle')}</h3>
                <p className="text-gray-700 text-sm leading-relaxed">{t('bk2026.format.speedText')}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Programme */}
      <section className="section bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-dark mb-4">
              {t('bk2026.programme.title')} <span className="text-lg font-normal text-gray-500">{t('bk2026.programme.timeSuffix')}</span>
            </h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <tbody>
                  <tr className="bg-gray-100">
                    <td colSpan={3} className="px-4 py-3 font-bold text-dark uppercase tracking-wide text-base border-b-2 border-gray-300">
                      {t('bk2026.programme.friday')}
                    </td>
                  </tr>
                  <tr className="border-b border-gray-100">
                    <td className="px-4 py-2 w-20 font-semibold text-gray-500 whitespace-nowrap">21:00</td>
                    <td className="px-2 py-2 w-20 text-gray-400 whitespace-nowrap">22:00</td>
                    <td className="px-4 py-2">{t('bk2026.programme.confirmAttendance')}</td>
                  </tr>
                  <tr className="bg-gray-100">
                    <td colSpan={3} className="px-4 py-3 font-bold text-dark uppercase tracking-wide text-base border-b-2 border-gray-300">
                      {t('bk2026.programme.saturday')}
                    </td>
                  </tr>
                  {([
                    ['9:00','10:00', t('bk2026.programme.registration')],
                    ['10:00','12:00', t('bk2026.programme.beginnerLesson')],
                    ['12:00','12:30', t('bk2026.programme.briefingLeadSpeed')],
                    ['12:30','16:00', t('bk2026.programme.leadQual')],
                    ['16:00','17:15', t('bk2026.programme.speedQual')],
                    ['17:15','18:00', t('bk2026.programme.speedFinals')],
                    ['18:00','19:00', t('bk2026.programme.diner')],
                    ['19:00','20:30', t('bk2026.programme.leadFinals')],
                    ['20:30','21:00', t('bk2026.programme.podiumLeadSpeed')],
                    ['21:00','—', t('bk2026.programme.party')],
                  ] as [string,string,string][]).map(([start, end, label], i) => (
                    <tr key={i} className="border-b border-gray-100">
                      <td className="px-4 py-2 font-semibold text-gray-500 whitespace-nowrap">{start}</td>
                      <td className="px-2 py-2 text-gray-400 whitespace-nowrap">{end}</td>
                      <td className="px-4 py-2">{label}</td>
                    </tr>
                  ))}
                  <tr className="bg-gray-100">
                    <td colSpan={3} className="px-4 py-3 font-bold text-dark uppercase tracking-wide text-base border-b-2 border-gray-300">
                      {t('bk2026.programme.sunday')}
                    </td>
                  </tr>
                  {([
                    ['09:00','11:00', t('bk2026.programme.warmUp')],
                    ['10:30','11:00', t('bk2026.programme.briefingBoulder')],
                    ['11:00','14:00', t('bk2026.programme.boulderComp')],
                    ['14:00','15:00', t('bk2026.programme.podiumBoulder')],
                  ] as [string,string,string][]).map(([start, end, label], i) => (
                    <tr key={i} className="border-b border-gray-100">
                      <td className="px-4 py-2 font-semibold text-gray-500 whitespace-nowrap">{start}</td>
                      <td className="px-2 py-2 text-gray-400 whitespace-nowrap">{end}</td>
                      <td className="px-4 py-2">{label}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* Technical Meeting */}
      <section className="section bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-dark mb-6">{t('bk2026.technical.title')}</h2>
            <div className="space-y-2 text-gray-700">
              <p><strong>{t('bk2026.technical.attendanceLabel')}</strong> {t('bk2026.technical.attendanceValue')}</p>
              <p><strong>{t('bk2026.technical.meetingLabel')}</strong> {t('bk2026.technical.meetingValue')}</p>
              <p><strong>{t('bk2026.technical.locationLabel')}</strong> {t('bk2026.technical.locationValue')}</p>
            </div>
            <div className="mt-4 bg-red-50 border-l-4 border-red-600 p-4 rounded-r-lg text-sm text-gray-700">
              {t('bk2026.technical.mandatory')}
            </div>
          </div>
        </div>
      </section>

      {/* Tickets */}
      <section className="section bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-dark mb-6">{t('bk2026.tickets.title')}</h2>
            <div className="bg-red-50 border-l-4 border-red-600 p-5 rounded-r-lg mb-6">
              <p className="text-gray-800 font-bold text-xl">{t('bk2026.tickets.price')}</p>
              <p className="text-gray-600 text-sm mt-1">{t('bk2026.tickets.dinnerIncluded')}</p>
            </div>
            <div className="space-y-3 text-gray-700 mb-6">
              <p><strong>{t('bk2026.tickets.typesLabel')}</strong> {t('bk2026.tickets.typesText')}</p>
              <p>
                <strong>{t('bk2026.tickets.purchaseLabel')}</strong>{' '}
                <a href="https://paraclimbing-be.weticket.io/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                  https://paraclimbing-be.weticket.io/
                </a>
              </p>
              <p>
                <strong>PayPal</strong> {t('bk2026.tickets.paypalText')}{' '}
                <a href="mailto:info@paraclimbing.be" className="text-blue-600 hover:underline">info@paraclimbing.be</a>.
              </p>
            </div>
            <a
              href="https://paraclimbing-be.weticket.io/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block bg-red-600 text-white font-bold py-3 px-8 rounded-lg hover:bg-red-700 transition-colors"
            >
              {t('bk2026.tickets.buyButton')}
            </a>
          </div>
        </div>
      </section>

      {/* Healthcare */}
      <section className="section bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-dark mb-6">{t('bk2026.health.title')}</h2>
            <p className="text-gray-700 mb-4">{t('bk2026.health.description')}</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
              {([
                { labelKey: 'bk2026.health.ambulance', number: '112' },
                { labelKey: 'bk2026.health.fire', number: '112' },
                { labelKey: 'bk2026.health.police', number: '101' },
                { labelKey: 'bk2026.health.euro', number: '112' },
              ] as {labelKey:string;number:string}[]).map(({ labelKey, number }) => (
                <div key={labelKey} className="bg-white rounded-lg shadow-sm p-4 text-center">
                  <p className="text-gray-500 text-xs mb-1">{t(labelKey)}</p>
                  <p className="text-2xl font-bold text-dark">{number}</p>
                </div>
              ))}
            </div>
            <p className="text-gray-700 text-sm">
              <strong>{t('bk2026.health.hospitalLabel')}</strong> AZ Rivierenland - campus Bornem, Kasteelstraat 23, 2880 Bornem, Belgium
            </p>
          </div>
        </div>
      </section>

      {/* Travel & Transport */}
      <section className="section bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-dark mb-8">{t('bk2026.travel.title')}</h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-lg font-bold text-dark mb-3">{t('bk2026.travel.planeTitle')}</h3>
                <p className="text-gray-700 text-sm mb-2">
                  <strong>Brussels Airport (BRU):</strong> {t('bk2026.travel.bruDist')}<br />
                  <a href="https://www.brusselsairport.be/en" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">brusselsairport.be</a>
                </p>
                <p className="text-gray-700 text-sm">
                  <strong>Antwerp Airport (ANR):</strong> {t('bk2026.travel.anrDist')}<br />
                  <a href="https://www.antwerp-airport.be" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">antwerp-airport.be</a>
                </p>
              </div>
              <div>
                <h3 className="text-lg font-bold text-dark mb-3">{t('bk2026.travel.trainTitle')}</h3>
                <p className="text-gray-700 text-sm">
                  {t('bk2026.travel.trainText')}<br />
                  <a href="https://www.belgiantrain.be/en" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">belgiantrain.be</a>
                </p>
              </div>
              <div>
                <h3 className="text-lg font-bold text-dark mb-3">{t('bk2026.travel.carTitle')}</h3>
                <p className="text-gray-700 text-sm mb-2">{t('bk2026.travel.carText')}</p>
                <a
                  href="https://www.google.com/maps/search/Klimax+Puurs+Belgium"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline text-sm"
                >
                  {t('bk2026.travel.googleMaps')}
                </a>
              </div>
              <div>
                <h3 className="text-lg font-bold text-dark mb-3">{t('bk2026.travel.busTitle')}</h3>
                <p className="text-gray-700 text-sm mb-2">{t('bk2026.travel.busStop')}</p>
                <a
                  href="https://www.delijn.be/nl/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline text-sm"
                >
                  {t('bk2026.travel.busInfo')}
                </a>
              </div>
            </div>
            <div className="mt-8 bg-blue-50 border-l-4 border-blue-400 p-4 rounded-r-lg text-sm text-gray-700">
              {t('bk2026.travel.accommodationText')}
            </div>
          </div>
        </div>
      </section>

      {/* Download CTA */}
      <section className="bg-dark py-16">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">{t('bk2026.cta.title')}</h2>
          <p className="text-gray-300 mb-8 max-w-xl mx-auto">{t('bk2026.cta.description')}</p>
          <a
            href="/assets/infosheet_puurs_full.pdf"
            download
            className="inline-block bg-red-600 text-white font-bold py-3 px-10 rounded-lg hover:bg-red-700 transition-colors text-lg"
          >
            {t('bk2026.cta.button')}
          </a>
        </div>
      </section>
        </>
      )}
      {/* Sponsors */}
      <SponsorsSection />
    </div>
  );
};

export default BK2026Page;
